import React, { Fragment, useCallback, useEffect, useState } from 'react';
import T from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash-es';
import SimpleFormat from '@16g/react-simple-format';

import Details from './Details';
import AddToList from '../../../AddToList';
import Button from '../../../Buttons/Button';
import MediaObject from '../../../MediaObject';
import CollapsibleSlot from '../../../CollapsibleSlot';
import { MARKET_LIST_TYPE } from '../../../AddToList/listTypeConfig';
import ErrorStateWithReload from '../../../ErrorStateWithReload';

import {
  requestAppQuickViewDetails,
  openPubQuickView,
  openCompanyQuickView,
  handleCleanupRequests,
} from '../../actions';
import { isDefaultQVSelector, isFinanceQVSelector, permissionsSelector } from '../../selectors';
import { isUnified, truncateTicker } from '../../../../utils/helpers';
import QuickViewCloseButton from '../../QuickViewCloseButton';

const Header = ({ app, widgets, settings, onClose }) => {
  const dispatch = useDispatch();

  const permissions = useSelector(permissionsSelector);
  const isFinanceQV = useSelector(isFinanceQVSelector);
  const isDefaultQV = useSelector(isDefaultQVSelector);

  const [detailsOpen, setDetailsOpen] = useState(false);

  const companyData = get(widgets, ['finance_insights', 'company']);

  const appPageLink = `${window.location.origin}/apps/${app.store}/${app.ref_no}/${
    permissions.defaultQuickViewPageName || 'performance'
  }`;
  const companyPageLink = `${window.location.origin}/companies/${companyData?.id}`;

  const onPubQuickViewOpen = useCallback((pub) => {
    dispatch(openPubQuickView(pub));
  }, []);

  const onCompanyQuickViewOpen = useCallback((company) => {
    dispatch(openCompanyQuickView(company));
  }, []);

  const decorateName = (name) => (
    <>
      <span className="text-truncate">{name}</span>
      {companyData?.bloomberg_ticker && (
        <span className="company-ticker-badge">{truncateTicker(companyData.bloomberg_ticker)}</span>
      )}
    </>
  );

  useEffect(() => setDetailsOpen(false), [app.ref_no]);

  useEffect(() => {
    if (detailsOpen) {
      dispatch(requestAppQuickViewDetails(isUnified(app.store) ? 'itunes_connect' : app.store));
    }
  }, [detailsOpen]);

  return (
    <Fragment>
      <div className="app-quick-view-header">
        <div className="app-quick-view-header__main-info">
          <MediaObject
            {...app}
            disableLink
            className="app-quick-view-header-media"
            appIconSize={80}
            appIconBordered
            withoutStore
            noPublisherData
            appendMediaBody={
              <div className="pub-company-links">
                {app.publisher_name && (
                  <a
                    className="publisher-name"
                    {...(permissions.allowAppQuickView
                      ? {
                          onClick: () =>
                            onPubQuickViewOpen({
                              store: app.cross_store_publisher_id ? 'unified' : app.publisher_store,
                              ref_no: app.cross_store_publisher_id || app.publisher_id,
                            }),
                        }
                      : {
                          href: `/publishers/${
                            app.cross_store_publisher_id ? 'unified' : app.publisher_store
                          }/${app.cross_store_publisher_id || app.publisher_id}`,
                        })}
                  >
                    <span className="publisher-name__label">Publisher:</span>
                    <SimpleFormat
                      text={app.publisher_name}
                      className="publisher-name__value text-truncate"
                    />
                  </a>
                )}
                {companyData?.name && (
                  <a
                    className="company-name"
                    onClick={() => onCompanyQuickViewOpen({ ref_no: companyData.id })}
                  >
                    <span className="company-name__label">Company:</span>
                    <span className="company-name__value text-truncate">{companyData.name}</span>
                  </a>
                )}
              </div>
            }
            decorateName={isFinanceQV ? decorateName : undefined}
            onPubQuickViewOpen={onPubQuickViewOpen}
          />

          <div className="app-quick-view-header-actions">
            {isDefaultQV && (
              <AddToList
                entities={[{ ...app }]}
                className="add-to-market-widget"
                buttonText="Add to Market"
                listType={MARKET_LIST_TYPE}
                variant="secondary"
                icon="hicon-plus-circle-thin"
              />
            )}
            {isFinanceQV && <QuickViewCloseButton onClose={onClose} />}
          </div>
        </div>
        <div className="app-quick-view-header__more-details">
          <a
            href="#!"
            className="app-quick-view-header__more-details-store-link link-default notranslate"
            onClick={(e) => {
              e.preventDefault();
              setDetailsOpen((prev) => !prev);
            }}
          >
            {!detailsOpen ? 'Show Details' : 'Hide Details'}
          </a>
        </div>
        <div className="app-quick-view-header__view-btns">
          <Button href={appPageLink} onClick={handleCleanupRequests}>
            View App
          </Button>
          {companyData?.id && (
            <Button variant="secondary" href={companyPageLink} onClick={handleCleanupRequests}>
              View Company{' '}
              {companyData.bloomberg_ticker && `(${truncateTicker(companyData.bloomberg_ticker)})`}
            </Button>
          )}
        </div>
      </div>
      <CollapsibleSlot
        show={detailsOpen}
        animationType=""
        className="app-quick-view-details-drawer"
      >
        {settings.error === true ? (
          <ErrorStateWithReload />
        ) : (
          <Details app={app} settings={settings} />
        )}
      </CollapsibleSlot>
    </Fragment>
  );
};
Header.propTypes = {
  app: T.object.isRequired,
  widgets: T.object,
  settings: T.object.isRequired,
  onClose: T.func.isRequired,
};

export default Header;
